// Social-media reframe presets + the crop math shared by the in-app preview and
// (mirrored in) the backend export worker. A movie's published "native" format
// is a 1920x1080 composed master; reframing crops a subject-centered window of
// the target aspect out of that master, then scales to the platform resolution.
//
// IMPORTANT: the geometry here MUST stay in sync with backend/api/socialExport.js
// so the in-app crop preview matches the exported file exactly.

// The aspect ratios the reframe crop preview can render. The crop math only
// needs the ratio (w/h); the export presets below carry the exact resolution.
export const ASPECT_RATIOS = [
  { id: '9x16', label: '9:16', w: 1080, h: 1920, hint: 'Full-screen vertical' },
  { id: '4x5', label: '4:5', w: 1080, h: 1350, hint: 'Tall feed post' },
  { id: '1x1', label: '1:1', w: 1080, h: 1080, hint: 'Square' },
  { id: '1.91x1', label: '1.91:1', w: 1146, h: 600, hint: 'Wide feed (IG native landscape)' },
  { id: '16x9', label: '16:9', w: 1920, h: 1080, hint: 'Cinematic landscape' },
];
export const aspectById = (id) => ASPECT_RATIOS.find((a) => a.id === id) || ASPECT_RATIOS[0];

// The full catalog of known, currently-supported formats per platform. Each
// preset: platform grouping, the placement it targets, its aspect ratio + exact
// output resolution, fps, video bitrate (kbps), an informational max duration
// (seconds), a `note` describing how it displays, and `recommended` for the
// best default. `aspect` links to ASPECT_RATIOS so selecting a format drives the
// live crop preview. IDs MUST stay in sync with backend/api/socialExport.js.
export const SOCIAL_PRESETS = [
  // ---- Instagram ----
  {
    id: 'instagram_reels',
    platform: 'Instagram',
    label: 'Reels / Stories',
    placement: 'Reels · Stories',
    aspect: '9x16',
    w: 1080,
    h: 1920,
    fps: 30,
    vBitrateK: 9000,
    maxSec: 180,
    recommended: true,
    note: 'Full-screen vertical — best reach. Keep key action clear of the top/bottom UI.',
  },
  {
    id: 'instagram_feed_portrait',
    platform: 'Instagram',
    label: 'Feed post (portrait)',
    placement: 'Feed',
    aspect: '4x5',
    w: 1080,
    h: 1350,
    fps: 30,
    vBitrateK: 6000,
    maxSec: 600,
    recommended: true,
    note: 'Tallest in-feed format — fills most of the screen without going full-screen. The recommended feed format.',
  },
  {
    id: 'instagram_feed_square',
    platform: 'Instagram',
    label: 'Feed post (square)',
    placement: 'Feed · Grid',
    aspect: '1x1',
    w: 1080,
    h: 1080,
    fps: 30,
    vBitrateK: 5500,
    maxSec: 600,
    note: 'Classic square — looks consistent on the profile grid.',
  },
  {
    id: 'instagram_feed_169',
    platform: 'Instagram',
    label: 'Feed post (16:9)',
    placement: 'Feed',
    aspect: '16x9',
    w: 1920,
    h: 1080,
    fps: 30,
    vBitrateK: 6000,
    maxSec: 600,
    note: 'Standard 16:9 (1.78:1) — sits inside IG’s feed range, so it posts uncropped as a landscape tile. A sideways clip stays sideways; viewers rotate their phone for full-screen.',
  },
  {
    id: 'instagram_feed_landscape',
    platform: 'Instagram',
    label: 'Feed post (widescreen)',
    placement: 'Feed',
    aspect: '1.91x1',
    w: 1146,
    h: 600,
    fps: 30,
    vBitrateK: 5000,
    maxSec: 600,
    note: 'Widest IG allows (1.91:1) — fills the most feed width but sits short vertically. Best for ultra-cinematic wides.',
  },
  // ---- TikTok ----
  {
    id: 'tiktok_vertical',
    platform: 'TikTok',
    label: 'Vertical',
    placement: 'For You feed',
    aspect: '9x16',
    w: 1080,
    h: 1920,
    fps: 30,
    vBitrateK: 9000,
    maxSec: 600,
    recommended: true,
    note: 'Native full-screen vertical — the default for TikTok.',
  },
  {
    id: 'tiktok_square',
    platform: 'TikTok',
    label: 'Square',
    placement: 'Feed',
    aspect: '1x1',
    w: 1080,
    h: 1080,
    fps: 30,
    vBitrateK: 6000,
    maxSec: 600,
    note: 'Square — plays smaller with space above/below in the player.',
  },
  {
    id: 'tiktok_landscape',
    platform: 'TikTok',
    label: 'Landscape',
    placement: 'Feed',
    aspect: '16x9',
    w: 1920,
    h: 1080,
    fps: 30,
    vBitrateK: 8000,
    maxSec: 600,
    note: 'Landscape — appears small unless the viewer rotates their phone. Good for rewatchable cinematic clips.',
  },
  // ---- X (Twitter) ----
  {
    id: 'x_landscape',
    platform: 'X',
    label: 'Landscape',
    placement: 'Timeline',
    aspect: '16x9',
    w: 1920,
    h: 1080,
    fps: 30,
    vBitrateK: 8000,
    maxSec: 140,
    recommended: true,
    note: 'Fills the inline timeline player — the best default for X. ~2:20 max (longer with Premium).',
  },
  {
    id: 'x_portrait',
    platform: 'X',
    label: 'Portrait',
    placement: 'Timeline · Immersive',
    aspect: '9x16',
    w: 1080,
    h: 1920,
    fps: 30,
    vBitrateK: 8000,
    maxSec: 140,
    note: 'Vertical — full-height in the immersive viewer.',
  },
  {
    id: 'x_square',
    platform: 'X',
    label: 'Square',
    placement: 'Timeline',
    aspect: '1x1',
    w: 1080,
    h: 1080,
    fps: 30,
    vBitrateK: 7000,
    maxSec: 140,
    note: 'Square — balanced for the inline timeline player.',
  },
];

export const presetById = (id) => SOCIAL_PRESETS.find((p) => p.id === id) || null;

// Format the informational max duration for display (e.g. "3 min", "2:20").
export function maxDurationLabel(sec) {
  if (!sec) return '';
  if (sec % 60 === 0) return `${sec / 60} min max`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')} max`;
}

// Group presets by platform for a tidy selection grid.
export function presetsByPlatform() {
  const out = [];
  for (const p of SOCIAL_PRESETS) {
    let g = out.find((x) => x.platform === p.platform);
    if (!g) {
      g = { platform: p.platform, presets: [] };
      out.push(g);
    }
    g.presets.push(p);
  }
  return out;
}

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Largest rectangle of the target aspect (w/h of the *output*) that fits inside
// the source frame. Returns integer {w,h} (even numbers for H.264).
export function cropRectFor(outW, outH, srcW, srcH) {
  const targetAR = outW / outH;
  const srcAR = srcW / srcH;
  let cw;
  let ch;
  if (targetAR <= srcAR) {
    ch = srcH;
    cw = Math.round(srcH * targetAR);
  } else {
    cw = srcW;
    ch = Math.round(srcW / targetAR);
  }
  cw = Math.min(srcW, cw - (cw % 2));
  ch = Math.min(srcH, ch - (ch % 2));
  return { w: cw, h: ch };
}

// Top-left of the crop window centered on a normalized subject center (cx,cy in
// 0..1 of the source), clamped so the window stays inside the frame.
export function waypointToXY(cx, cy, cropW, cropH, srcW, srcH) {
  const x = clamp(cx * srcW - cropW / 2, 0, srcW - cropW);
  const y = clamp(cy * srcH - cropH / 2, 0, srcH - cropH);
  return { x: Math.round(x), y: Math.round(y) };
}

// Linear-interpolated subject center at time t from a sorted waypoint list.
// Falls back to frame center {0.5,0.5} when there are no waypoints.
export function centerAt(waypoints, t) {
  const wp = waypoints || [];
  if (!wp.length) return { cx: 0.5, cy: 0.5 };
  if (t <= wp[0].t) return { cx: wp[0].cx, cy: wp[0].cy };
  const last = wp[wp.length - 1];
  if (t >= last.t) return { cx: last.cx, cy: last.cy };
  for (let i = 1; i < wp.length; i += 1) {
    if (t <= wp[i].t) {
      const a = wp[i - 1];
      const b = wp[i];
      const span = b.t - a.t || 1;
      const f = (t - a.t) / span;
      return { cx: a.cx + (b.cx - a.cx) * f, cy: a.cy + (b.cy - a.cy) * f };
    }
  }
  return { cx: last.cx, cy: last.cy };
}

// Linear-interpolated zoom `scale` (crop size, 0.25..1 of the aspect-fit window)
// at time t from zoom keyframes [{t, scale}]. Falls back to `baseScale` (the
// clip's single zoom) when there are no keyframes. Mirrors centerAt for the
// crop SIZE so the window can push in / pull out over a clip.
export function scaleAt(zoomKeys, baseScale, t) {
  const b = clamp(Number(baseScale) || 1, 0.25, 1);
  const zk = zoomKeys || [];
  if (!zk.length) return b;
  const cs = (s) => clamp(Number(s) || b, 0.25, 1);
  if (t <= zk[0].t) return cs(zk[0].scale);
  const last = zk[zk.length - 1];
  if (t >= last.t) return cs(last.scale);
  for (let i = 1; i < zk.length; i += 1) {
    if (t <= zk[i].t) {
      const a = zk[i - 1];
      const c = zk[i];
      const span = c.t - a.t || 1;
      const f = (t - a.t) / span;
      return clamp(cs(a.scale) + (cs(c.scale) - cs(a.scale)) * f, 0.25, 1);
    }
  }
  return cs(last.scale);
}

// Thin a dense detection path down to editable keyframes: keep the first and
// last points plus one every `intervalSec`, but always preserve local turning
// points (where the subject reverses direction along either axis by more than
// `minDelta`) so the motion isn't flattened. Returns [{t, cx, cy}].
export function thinPath(points, intervalSec = 0.5, minDelta = 0.04) {
  const a = (points || [])
    .filter((p) => p && Number.isFinite(p.t))
    .map((p) => ({ t: p.t, cx: p.cx, cy: p.cy }))
    .sort((x, y) => x.t - y.t);
  if (a.length <= 2) return a;
  const turn = (i, key) => {
    const prev = a[i - 1][key];
    const cur = a[i][key];
    const next = a[i + 1][key];
    const d1 = cur - prev;
    const d2 = next - cur;
    return d1 * d2 < 0 && (Math.abs(d1) > minDelta || Math.abs(d2) > minDelta);
  };
  const out = [a[0]];
  let lastT = a[0].t;
  for (let i = 1; i < a.length - 1; i += 1) {
    if (a[i].t - lastT >= intervalSec || turn(i, 'cx') || turn(i, 'cy')) {
      out.push(a[i]);
      lastT = a[i].t;
    }
  }
  out.push(a[a.length - 1]);
  return out;
}

// Resample waypoints to a fixed rate and apply the export smoothing so pans
// preview exactly as they bake. MUST mirror backend/shared/reframePath.js:
//  • time-based EMA — the saved smoothing alpha is defined at REFRAME_REF_HZ,
//    so sampling at any hz converts it (alphaEff = 1-(1-a)^(refHz/hz)) and the
//    glide is rate-independent (the bake samples at the output fps, e.g. 72).
//  • pan-speed limit — the smoothed center's velocity is clamped to
//    REFRAME_MAX_PAN frame-widths/sec so EMA catch-up never reads as a
//    too-fast camera pan. (Backend value lives in config/grading
//    render.reframeMaxPanSpeed; keep these defaults in sync.)
export const REFRAME_REF_HZ = 30;
export const REFRAME_MAX_PAN = 0.35;

// DENSE SUBJECT PATH — mirror of backend/shared/reframePath.js
// anchoredDenseWaypoints (keep identical): blend a clip's raw YOLO track
// samples (dense) with the user's edited keyframes (sparse, authoritative).
// User keyframes win exactly at their own times; between them the path follows
// the track's shape shifted by the interpolated correction (user − track).
export function anchoredDenseWaypoints(trackSamples, userWps) {
  const track = (trackSamples || []).filter((p) => p && Number.isFinite(p.t));
  const user = (userWps || []).filter((p) => p && Number.isFinite(p.t));
  if (track.length < 2) return user;
  if (!user.length) return track.map((p) => ({ t: p.t, cx: p.cx, cy: p.cy }));
  const at = (arr, t) => {
    if (t <= arr[0].t) return arr[0];
    const last = arr[arr.length - 1];
    if (t >= last.t) return last;
    for (let i = 1; i < arr.length; i += 1) {
      if (t <= arr[i].t) {
        const a = arr[i - 1];
        const b = arr[i];
        const f = (t - a.t) / (b.t - a.t || 1);
        return { cx: a.cx + (b.cx - a.cx) * f, cy: a.cy + (b.cy - a.cy) * f };
      }
    }
    return last;
  };
  const deltas = user
    .map((w) => {
      const tr = at(track, w.t);
      return { t: w.t, cx: w.cx - tr.cx, cy: w.cy - tr.cy };
    })
    .sort((a, b) => a.t - b.t);
  const merged = track
    .map((p) => {
      const d = at(deltas, p.t);
      return {
        t: Math.round(p.t * 1000) / 1000,
        cx: Math.min(1, Math.max(0, p.cx + d.cx)),
        cy: Math.min(1, Math.max(0, p.cy + d.cy)),
      };
    })
    .concat(user.map((w) => ({ t: Math.round(w.t * 1000) / 1000, cx: w.cx, cy: w.cy })))
    .sort((a, b) => a.t - b.t);
  return merged.filter((p, i) => i === 0 || p.t - merged[i - 1].t > 1e-3);
}
export function densifyWaypoints(waypoints, duration, hz = 10, alpha = 0.35) {
  const out = [];
  if (!duration || duration <= 0) return out;
  const step = 1 / hz;
  const a = 1 - (1 - Math.min(1, Math.max(0.01, alpha))) ** (REFRAME_REF_HZ / hz);
  const maxD = REFRAME_MAX_PAN * step;
  let ex = null;
  let ey = null;
  for (let t = 0; t <= duration + 1e-6; t += step) {
    const { cx, cy } = centerAt(waypoints, t);
    if (ex == null) {
      ex = cx; ey = cy;
    } else {
      let dx = a * (cx - ex);
      let dy = a * (cy - ey);
      const m = Math.hypot(dx, dy);
      if (m > maxD) { dx *= maxD / m; dy *= maxD / m; }
      ex += dx;
      ey += dy;
    }
    out.push({ t: Math.round(t * 1000) / 1000, cx: ex, cy: ey });
  }
  return out;
}
